/**
 * SmartTimeInput - Google Calendar-style time input with natural language parsing
 *
 * Features:
 * - Free-text input with smart parsing
 * - Natural language support ("2pm", "noon", "quarter past 3")
 * - Autocomplete suggestions
 * - Keyboard navigation
 * - Temporal API integration
 * - Full accessibility support
 */

import * as React from 'react'

import { Input } from '../Input'
import { Text } from '../Text'
import { VisuallyHidden } from '../VisuallyHidden'
import { ClockIcon } from '../icons'

import { useSmartTimeCombobox } from './hooks/useSmartTimeCombobox'
import { NativeTimeInput } from './components/NativeTimeInput'
import { SuggestionMenu } from './components/SuggestionMenu'
import { useMergeRefs } from '../../hooks/useMergeRefs'

import type { SmartTimeInputProps } from './types'
import styles from './SmartTimeInput.module.css'

/**
 * SmartTimeInput Component
 */
const SmartTimeInput = React.forwardRef<
  HTMLInputElement,
  SmartTimeInputProps & { 'data-testid'?: string }
>((props, forwardedRef) => {
  const {
    disabled = false,
    readOnly = false,
    size = 'medium',
    className,
    style,
    prefix,
    suffix,
    'data-testid': dataTestId,
  } = props

  const combobox = useSmartTimeCombobox(props)
  const mergedRef = useMergeRefs(forwardedRef, combobox.inputRef)

  const defaultPrefix = prefix ?? (
    <ClockIcon size="small" aria-hidden="true" focusable="false" />
  )

  if (combobox.detectedMobile) {
    return (
      <NativeTimeInput
        ref={mergedRef}
        value={combobox.effectiveValue}
        onChange={combobox.handleNativeTimeChange}
        onBlur={combobox.handleBlur}
        disabled={disabled}
        readOnly={readOnly}
        size={size}
        id={props.id}
        name={props.name}
        aria-label={props['aria-label']}
        aria-describedby={props['aria-describedby']}
        min={props.min}
        max={props.max}
        step={combobox.effectiveStep}
        error={props.error}
        validationError={combobox.validationError}
        prefix={defaultPrefix}
        suffix={suffix}
        className={className}
        style={style}
        data-testid={dataTestId}
      />
    )
  }

  return (
    <div ref={combobox.wrapperRef} className={styles.container}>
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {combobox.liveRegionMessage}
      </VisuallyHidden>

      <SuggestionMenu
        suggestions={combobox.suggestions}
        activeIndex={combobox.activeIndex}
        matchIndex={combobox.matchIndex}
        userNavigated={combobox.userNavigated}
        isOpen={combobox.isOpen}
        onOpenChange={combobox.handleDropdownOpenChange}
        onSuggestionSelect={combobox.handleSuggestionSelect}
        onActiveIndexChange={combobox.actions.setActiveIndex}
        inputRef={combobox.inputRef}
        listboxId={combobox.listboxId}
        shouldScrollToActive={combobox.shouldScrollToActive}
        inputValue={combobox.inputValue}
      >
        <div>
          <Input
            ref={mergedRef}
            type="text"
            value={combobox.inputValue}
            onChange={readOnly ? undefined : combobox.handleInputChange}
            onFocus={readOnly ? undefined : combobox.handleFocus}
            onBlur={readOnly ? undefined : combobox.handleBlur}
            onKeyDown={readOnly ? undefined : combobox.handleKeyDown}
            placeholder={combobox.placeholderHint}
            disabled={disabled}
            readOnly={readOnly}
            size={size}
            id={props.id}
            name={props.name}
            {...combobox.inputAriaProps}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            prefix={defaultPrefix}
            data-testid={dataTestId}
            suffix={suffix}
            className={className}
            style={style}
          />
        </div>
      </SuggestionMenu>

      {combobox.validationError ? (
        <Text
          id={combobox.errorId}
          size="sm"
          color="critical"
          className={styles.errorMessage}
          role="alert"
        >
          {combobox.validationError.message}
        </Text>
      ) : null}
    </div>
  )
})

SmartTimeInput.displayName = 'SmartTimeInput'

export { SmartTimeInput }
export type { SmartTimeInputProps }
